import { createAsyncThunk } from "@reduxjs/toolkit";
import { ApiResponseData, client, ExtendedAxiosRequestConfig } from "@api";
import { EmailAnalysisParticipant, EmailAnalysisResult, StatusCode } from "@types";
import { RootState } from "../store";

export const analyzeThread = createAsyncThunk<
  EmailAnalysisResult,
  {
    conversationId: string;
    messageId: string;
    outlookProfileEmail: string;
    participants: EmailAnalysisParticipant[];
    subject: string;
  }
>(
  "analysis/analyzeThread",
  async (
    { conversationId, messageId, outlookProfileEmail, participants, subject },
    { getState, fulfillWithValue, rejectWithValue, signal }
  ) => {
    try {
      const {
        auth: { user },
      } = getState() as RootState;

      const mappedParticipants = (participants || []).map((participant) => {
        const { email, metadata, type } = participant;
        const currentUserMetadata = {
          userId: user?.userId,
          orgId: user?.orgId,
        };

        return {
          email,
          metadata: {
            ...metadata,
            ...(email === user?.email || email === outlookProfileEmail ? currentUserMetadata : {}),
          },
          type,
        };
      });

      const requestBody = {
        threadData: {
          platformThreadId: conversationId || "",
          participants: mappedParticipants,
          subject: subject || "",
          source: "outlookApi",
        },
        platform: "outlook",
        platformMessageIdToAnalyze: messageId || "",
      };

      const response = await client.post<ApiResponseData<EmailAnalysisResult>>(
        `/services/orgs/${user?.orgId}/users/${user?.userId}/threads/analyze`,
        requestBody,
        {
          signal,
          isEncrypted: true,
          // TODO: Remove after full transition to new encryption approach
          params: {
            encV2: true,
          },
        } as ExtendedAxiosRequestConfig
      );

      return fulfillWithValue(response.data.payload);
    } catch (error) {
      return rejectWithValue({
        permissionError: error?.response?.status === StatusCode.Forbidden,
        recipientsError: error?.response?.status === StatusCode.NoRecipients,
      });
    }
  },
  {
    condition: (_, { getState }) => {
      const {
        auth: { user },
      } = getState() as RootState;

      return !!user;
    },
  }
);
